import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { NavigationService } from '@root/search/services/navigation.service';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { PropertyIdentifier, PropertyInformation } from '@store/search-board/search-board.model';
import {
  ClearAndSetViewAssessorMap,
  ClearAndSetViewBuildingPermits,
  ClearAndSetViewBuildingSketch,
  ClearAndSetViewCommunityInsights,
  ClearAndSetViewForeclosure,
  SetNonRefundable
} from '@store/orders/orders.actions';
import { Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { CartItem } from '@store/orders/orders.model';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { CartItemKeys } from '../cart/cart.constants';

@Component({
  selector: 'rlst-view-confirmation',
  templateUrl: './view-confirmation.component.html',
  styleUrls: ['./view-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewConfirmationComponent implements OnInit {
  public property: string;
  public productId: string;
  public apn: string;
  private propertyIdentifier: PropertyIdentifier;
  private docNum: string;

  constructor(
    private readonly navigationService: NavigationService,
    private readonly store$: Store<AppState>,
    private readonly dialogRef: MatDialogRef<ViewConfirmationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      property: string,
      orderItem: CartItem,
      isReportPage?: boolean,
      documentImageParam?: string,
      buildingPermitsParam?: { clip: string, apn: string, countyId: string },
      assessorMapParam?: { propertyIdentifier: PropertyIdentifier },
      buildingSketchParam?: { propertyIdentifier: PropertyIdentifier, imageWidth: number, imageHeight: number },
      foreclosureParam?: { propertyInformation: PropertyInformation },
    },
    private readonly mixPanelService: MixpanelService
  ) {
    this.propertyIdentifier = JSON.parse(this.data.property);
  }

  ngOnInit(): void {
    this.property = this.data.property;
    this.productId = this.data.orderItem.productId;
  }

  public goToReport() {
    let apn: string = this.propertyIdentifier.apn;
    if (this.data.orderItem.productId === CartItemKeys.DOCUMENT_IMAGE) {
      this.data.documentImageParam = this.data.documentImageParam ?? JSON.parse(this.data.property).documentImageParam;
      this.docNum = this.getDocumentNumber(this.data.documentImageParam);
      apn = this.docNum;
    }

    const item = { ...this.data.orderItem, apn: apn, productId: this.productId };
    const communityInsightsReportIds: Array<string> = ['REST', 'SCHOOL', 'ECDEM', 'TRFO'];
    if (communityInsightsReportIds.includes(this.productId)) {
      this.store$.dispatch(new ClearAndSetViewCommunityInsights({
        item,
        propertyIdentifier: JSON.parse(this.property),
      }));
      this.logMixPanel('Confirmed - not refundable.');
      this.dialogRef.close();
      return;
    }

    if (this.data.buildingPermitsParam) {
      this.store$.dispatch(new ClearAndSetViewBuildingPermits({
        item,
        ...this.data.buildingPermitsParam,
      }));
      this.logMixPanel('Confirmed - not refundable.');
      this.dialogRef.close();
      return;
    }

    if (this.data.assessorMapParam) {
      this.store$.dispatch(new ClearAndSetViewAssessorMap({
        item,
        ...this.data.assessorMapParam,
      }));
      this.logMixPanel('Confirmed - not refundable.');
      this.dialogRef.close();
      return;
    }

    if (this.data.buildingSketchParam) {
      this.store$.dispatch(new ClearAndSetViewBuildingSketch({
        item,
        ...this.data.buildingSketchParam,
      }));
      this.logMixPanel('Confirmed - not refundable.');
      this.dialogRef.close();
      return;
    }

    if (this.data.foreclosureParam) {
      this.store$.dispatch(new ClearAndSetViewForeclosure({
        item,
        ...this.data.foreclosureParam,
      }));
      this.logMixPanel('Confirmed - not refundable.');
      this.dialogRef.close();
      return;
    }

    this.store$.dispatch(new SetNonRefundable(item, this.data.documentImageParam, this.data.isReportPage));

    if (!this.data.isReportPage) {
      this.navigationService.setUpPropertyForReport(JSON.parse(this.property), this.data.orderItem.displayName, this.data.orderItem, true);
    }

    this.logMixPanel('Confirmed - not refundable.');
    this.dialogRef.close();
  }

  public cancel() {
    this.logMixPanel('Canceled - did not confirm.');
    this.dialogRef.close(true);
  }

  private logMixPanel(reportState: string) {
    this.mixPanelService.track('Orders', {
      action: 'View Purchased Report Link',
      reportState: reportState,
      apn: this.propertyIdentifier.apn,
      docNum: this.docNum ?? '',
      parcelId: this.propertyIdentifier.parcelId,
      fipsCode: this.propertyIdentifier.fipsCode,
      item: this.productId,
      report: this.data.orderItem.displayName
    });
  }

  private getDocumentNumber(payload: string): any {
    const params = payload.split('&');
    const paramsMap = new Map();

    if(params) {
      params.forEach(param => {
        const pair = param.split('=');

        const key = decodeURIComponent(pair[0]);
        let value = '';

        if(pair.length > 1) {
          value = decodeURIComponent(pair[1]);
        }

        paramsMap.set(key, value);
      });
    }

    return paramsMap.get('doc1') ? paramsMap.get('doc1') : paramsMap.get('doc2');
  }
}
